// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const STAGES = ['New Lead', 'Contacted', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const STAGE_COLORS = { 'New Lead': '#6366f1', 'Contacted': '#3b82f6', 'Qualified': '#8b5cf6', 'Proposal': '#f59e0b', 'Negotiation': '#f97316', 'Won': '#10b981', 'Lost': '#ef4444' };

const DEMO_CONTACTS = [
  { id: 1, name: 'Sarah Mitchell', email: 'sarah@techcorp.ca', phone: '(403) 555-0142', company: 'TechCorp', stage: 'Qualified', value: '$12,500', lastContact: 'Feb 19', source: 'Website', score: 82 },
  { id: 2, name: 'James Rodriguez', email: 'james@buildit.ca', phone: '(587) 555-0198', company: 'BuildIt Inc', stage: 'Proposal', value: '$8,200', lastContact: 'Feb 18', source: 'Ad Campaign', score: 74 },
  { id: 3, name: 'Emily Chen', email: 'echen@greenfield.ca', phone: '(403) 555-0231', company: 'Greenfield Co', stage: 'New Lead', value: '$5,000', lastContact: 'Feb 20', source: 'Referral', score: 65 },
  { id: 4, name: 'Mike Thompson', email: 'mike@westcoast.ca', phone: '(250) 555-0177', company: 'West Coast Ltd', stage: 'Negotiation', value: '$22,000', lastContact: 'Feb 17', source: 'Social Media', score: 91 },
  { id: 5, name: 'Lisa Park', email: 'lisa@innovate.ca', phone: '(604) 555-0311', company: 'Innovate Labs', stage: 'Contacted', value: '$3,500', lastContact: 'Feb 16', source: 'Email', score: 48 },
  { id: 6, name: 'David Wilson', email: 'dwilson@prairieag.ca', phone: '(306) 555-0289', company: 'Prairie Ag', stage: 'Won', value: '$15,800', lastContact: 'Feb 14', source: 'Website', score: 95 },
  { id: 7, name: 'Karen Brown', email: 'kbrown@cityserv.ca', phone: '(780) 555-0144', company: 'City Services', stage: 'Lost', value: '$7,000', lastContact: 'Feb 10', source: 'Cold Call', score: 32 },
];

const ACTIVITY_LOG = [
  { id: 1, type: 'email', text: 'Sent proposal email', contact: 'Sarah Mitchell', time: '2 hours ago' },
  { id: 2, type: 'call', text: 'Follow-up call - discussed pricing', contact: 'Mike Thompson', time: '4 hours ago' },
  { id: 3, type: 'meeting', text: 'Demo presentation completed', contact: 'James Rodriguez', time: 'Yesterday' },
  { id: 4, type: 'note', text: 'Interested in premium tier', contact: 'Emily Chen', time: 'Yesterday' },
  { id: 5, type: 'form', text: 'Submitted contact form on website', contact: 'Lisa Park', time: '2 days ago' },
];

const AUTOMATION_RULES = [
  { id: 1, trigger: 'Lead score above 80', action: 'Notify sales team via email', status: 'Active' },
  { id: 2, trigger: 'No activity in 7 days', action: 'Send automated follow-up email', status: 'Active' },
  { id: 3, trigger: 'Deal marked as Won', action: 'Trigger onboarding email sequence', status: 'Active' },
  { id: 4, trigger: 'New form submission', action: 'Create lead and assign to team', status: 'Paused' },
];

function CRMPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('pipeline');
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const getScoreColor = (score) => score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const getActivityIcon = (type) => ({ email: '\uD83D\uDCE7', call: '\uD83D\uDCDE', meeting: '\uD83D\uDCC5', note: '\uD83D\uDCDD', form: '\uD83D\uDCCB' }[type] || '\uD83D\uDD35');

  const pipelineValue = DEMO_CONTACTS.filter(c => !['Won', 'Lost'].includes(c.stage)).reduce((sum, c) => sum + parseInt(c.value.replace(/[$,]/g, '')), 0);
  const wonValue = DEMO_CONTACTS.filter(c => c.stage === 'Won').reduce((sum, c) => sum + parseInt(c.value.replace(/[$,]/g, '')), 0);

  const filteredContacts = DEMO_CONTACTS.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderStats = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
      {[
        { label: 'Pipeline Value', value: `$${pipelineValue.toLocaleString()}`, color: '#6366f1' },
        { label: 'Won This Month', value: `$${wonValue.toLocaleString()}`, color: '#10b981' },
        { label: 'Conversion Rate', value: '28.6%', color: '#3b82f6' },
        { label: 'Avg Deal Time', value: '14 days', color: '#f59e0b' },
        { label: 'Active Leads', value: DEMO_CONTACTS.filter(c => !['Won', 'Lost'].includes(c.stage)).length.toString(), color: '#8b5cf6' },
      ].map(s => (
        <div key={s.label} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{s.label}</p>
          <p style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</p>
        </div>
      ))}
    </div>
  );

  const renderPipeline = () => (
    <div>
      {renderStats()}
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Pipeline Board</h2>
      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12 }}>
        {STAGES.filter(s => s !== 'Won' && s !== 'Lost').map(stage => {
          const stageContacts = DEMO_CONTACTS.filter(c => c.stage === stage);
          return (
            <div key={stage} style={{ minWidth: 240, flex: '0 0 240px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_COLORS[stage] }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{stage}</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>{stageContacts.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {stageContacts.map(c => (
                  <div key={c.id} onClick={() => setSelectedContact(c)} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, cursor: 'pointer', borderLeft: `3px solid ${STAGE_COLORS[stage]}` }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{c.name}</h4>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{c.company}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: '#6366f1' }}>{c.value}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: getScoreColor(c.score), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700 }}>{c.score}</div>
                      </div>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>Last: {c.lastContact} | {c.source}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderContacts = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search contacts..." style={{ ...inputStyle, maxWidth: 400 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ padding: '8px 16px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Import CSV</button>
          <button style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>+ Add Contact</button>
        </div>
      </div>
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr 80px 80px 80px 80px', padding: '12px 20px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
          <span>Name</span><span>Email</span><span>Company</span><span>Stage</span><span>Value</span><span>Score</span><span>Source</span><span>Last</span>
        </div>
        {filteredContacts.map(c => (
          <div key={c.id} onClick={() => setSelectedContact(c)} style={{ display: 'grid', gridTemplateColumns: '1.5fr 2fr 1fr 1fr 80px 80px 80px 80px', padding: '12px 20px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{c.name}</span>
            <span style={{ color: 'var(--text-muted)' }}>{c.email}</span>
            <span style={{ color: 'var(--text-muted)' }}>{c.company}</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: STAGE_COLORS[c.stage] }}>{c.stage}</span>
            <span style={{ fontWeight: 600, color: '#6366f1' }}>{c.value}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: getScoreColor(c.score), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 700 }}>{c.score}</div>
            </div>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.source}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.lastContact}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderActivity = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Activity Feed</h2>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['Log Call', 'Send Email', 'Schedule Meeting', 'Add Note'].map(a => (
          <button key={a} style={{ padding: '8px 16px', background: 'var(--card)', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>{a}</button>
        ))}
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {ACTIVITY_LOG.map(a => (
          <div key={a.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 20 }}>{getActivityIcon(a.type)}</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 2 }}>{a.text}</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.contact}</p>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{a.time}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAutomation = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>Automation Rules</h2>
      <div style={{ display: 'grid', gap: 10 }}>
        {AUTOMATION_RULES.map(r => (
          <div key={r.id} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>When: {r.trigger}</p>
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Then: {r.action}</p>
            </div>
            <span style={{ fontSize: 12, fontWeight: 500, padding: '4px 10px', borderRadius: 6, background: r.status === 'Active' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: r.status === 'Active' ? '#10b981' : '#f59e0b' }}>{r.status}</span>
            <button style={{ padding: '6px 12px', background: 'transparent', color: '#6366f1', border: '1px solid #6366f1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Edit</button>
          </div>
        ))}
      </div>
      <button style={{ marginTop: 16, padding: '10px 20px', background: 'transparent', color: '#6366f1', border: '1px dashed #6366f1', borderRadius: 8, fontSize: 14, cursor: 'pointer', width: '100%' }}>+ Add Automation Rule</button>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{'\uD83D\uDCCA'} CRM & Leads</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 600 }}>Track leads, manage your pipeline, and close more deals.</p>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {['pipeline', 'contacts', 'activity', 'automation'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 20px', background: tab === t ? '#6366f1' : 'var(--card)', color: tab === t ? '#fff' : 'var(--text-muted)', border: tab === t ? 'none' : '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>{t}</button>
        ))}
      </div>
      {tab === 'pipeline' && renderPipeline()}
      {tab === 'contacts' && renderContacts()}
      {tab === 'activity' && renderActivity()}
      {tab === 'automation' && renderAutomation()}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };
const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' };

export default CRMPage;
